import { Capacitor } from '@capacitor/core';
import {
  type ActionPerformed,
  PushNotifications,
  type PushNotificationSchema,
  type RegistrationError,
  type Token,
} from '@capacitor/push-notifications';
import { FCM } from '@capacitor-community/fcm';
import {
  NotificationsApi,
  DevicePushPlatform,
} from '@up2stay/up2promo-api-client';

import { isCapacitorNative } from '@/core/utils/capacitor.utils';

import { env } from '@/core/env';

const REGISTERED_TOKEN_KEY = 'push-notification-token';

let listenersAttached = false;
let currentToken: string | null = null;

function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(REGISTERED_TOKEN_KEY);
}

function setStoredToken(token: string | null) {
  if (typeof window === 'undefined') return;
  if (token) {
    sessionStorage.setItem(REGISTERED_TOKEN_KEY, token);
  } else {
    sessionStorage.removeItem(REGISTERED_TOKEN_KEY);
  }
}

function getPlatform(): 'ios' | 'android' | null {
  if (!isCapacitorNative()) return null;
  const platform = Capacitor.getPlatform();
  if (platform === 'ios' || platform === 'android') return platform;
  return null;
}

/** Capacitor returns APNs hex on iOS; our backend sends via FCM and needs the FCM token. */
async function resolveFcmRegistrationToken(
  capacitorToken: string,
): Promise<string> {
  if (getPlatform() === 'ios') {
    const { token } = await FCM.getToken();
    return token;
  }
  return capacitorToken;
}

async function syncTokenWithBackend(token: string): Promise<void> {
  const platform = getPlatform();
  if (!platform) return;

  await NotificationsApi.registerDevice({
    body: {
      token,
      platform:
        platform === 'ios'
          ? DevicePushPlatform.IOS
          : DevicePushPlatform.ANDROID,
      appVersion: env.VITE_APP_VERSION ?? null,
    },
  });
  currentToken = token;
  setStoredToken(token);
}

export async function revokePushTokenOnLogout(): Promise<void> {
  const token = currentToken ?? getStoredToken();
  if (!token) return;

  try {
    await NotificationsApi.revokeDevice({ body: { token } });
  } catch {
    // Best effort during logout
  } finally {
    currentToken = null;
    setStoredToken(null);
  }
}

export type PushPermissionStatus =
  | 'prompt'
  | 'prompt-with-rationale'
  | 'granted'
  | 'denied';

export async function getPushPermissionStatus(): Promise<PushPermissionStatus> {
  if (!isCapacitorNative()) return 'denied';
  const status = await PushNotifications.checkPermissions();
  return status.receive;
}

export async function requestPushPermissionAndRegister(options?: {
  /** Re-check after the user may have enabled notifications in system settings. */
  afterExternalSettings?: boolean;
}): Promise<{
  granted: boolean;
  token?: string;
}> {
  if (!isCapacitorNative()) {
    return { granted: false };
  }

  const current = await PushNotifications.checkPermissions();
  let receive = current.receive;

  if (
    receive === 'prompt' ||
    receive === 'prompt-with-rationale' ||
    (options?.afterExternalSettings && receive === 'denied')
  ) {
    const requested = await PushNotifications.requestPermissions();
    receive = requested.receive;
  }

  if (receive !== 'granted') {
    return { granted: false };
  }

  await PushNotifications.register();
  return { granted: true };
}

export type PushNotificationHandlers = {
  onAction?: (action: ActionPerformed) => void;
  onReceived?: (notification: PushNotificationSchema) => void;
};

export async function initializePushNotifications(
  handlers: PushNotificationHandlers,
): Promise<void> {
  if (!isCapacitorNative() || listenersAttached) {
    return;
  }

  listenersAttached = true;
  currentToken = getStoredToken();

  await PushNotifications.addListener('registration', async (event: Token) => {
    try {
      const fcmToken = await resolveFcmRegistrationToken(event.value);
      await syncTokenWithBackend(fcmToken);
    } catch (error) {
      console.error('[Push] Failed to register device token:', error);
    }
  });

  await PushNotifications.addListener(
    'registrationError',
    (error: RegistrationError) => {
      console.error('[Push] Registration error:', error);
    },
  );

  if (handlers.onReceived) {
    await PushNotifications.addListener(
      'pushNotificationReceived',
      handlers.onReceived,
    );
  }

  if (handlers.onAction) {
    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      handlers.onAction,
    );
  }

  const permission = await PushNotifications.checkPermissions();
  if (permission.receive === 'granted') {
    await PushNotifications.register();
  }
}

export function getCurrentPushToken(): string | null {
  return currentToken ?? getStoredToken();
}
